import React, { useEffect, useState, useCallback, useRef } from 'react';
import { usePermissions } from 'react-admin';
import { httpClient } from '../utils/dataProvider';
import { hasAccess } from '../utils/permissions';
import { API_BASE_URL } from '../utils/config';
import {
    PageShell, PageHeader, Surface, MetricCard, StatePanel, Empty,
    StatusPill, Search, Avatar,
} from '../components/ui';
import { describeUserAgent } from './GuestVisitors';

const RANGES = [7, 30, 90];

export const PLACEHOLDER = '—';

/**
 * Resolve a display name from the user object, following the admin convention:
 * displayNameEn → displayNameAr → displayName → nameEn → nameAr → name → '(unnamed)'
 */
const resolveName = (u) => {
    if (!u) return '(unnamed)';
    return u.displayNameEn || u.displayNameAr || u.displayName
        || u.nameEn || u.nameAr || u.name || '(unnamed)';
};

const fmtInt = n => Number(n || 0).toLocaleString('en-US');

// Several user fields come from a backend change that may not be deployed
// yet, so cells only ever print strings and finite numbers. Anything else
// (missing, null, objects, NaN) becomes the placeholder.
export const cellText = value => {
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : PLACEHOLDER;
    if (typeof value !== 'string') return PLACEHOLDER;
    const text = value.trim();
    return text ? text : PLACEHOLDER;
};

export const fmtCount = n => (typeof n === 'number' && Number.isFinite(n) ? fmtInt(n) : PLACEHOLDER);

// City/country are often unset. Render only what exists so the cell never
// degrades into a bare pair of dashes.
const fmtLocation = (city, country) => {
    const parts = [city, country].map(v => (typeof v === 'string' ? v.trim() : '')).filter(Boolean);
    return parts.length ? parts.join(', ') : 'Location not set';
};

const fmtDate = iso => {
    if (!iso) return PLACEHOLDER;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return PLACEHOLDER;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

// Verified cell: identityVerified + verificationLevel (0/1/2). Missing both → placeholder.
export const verifiedLabel = user => {
    const u = user || {};
    const level = typeof u.verificationLevel === 'number' && Number.isFinite(u.verificationLevel)
        ? u.verificationLevel : null;
    if (level !== null && level > 0) return `L${level}`;
    if (u.identityVerified === true) return 'Yes';
    if (u.identityVerified === false || level === 0) return 'No';
    return PLACEHOLDER;
};

/**
 * Platform + device guess from the user agent of the last LOGIN row.
 * The mobile app signs in through React Native's networking stack, which
 * sends the native client's default agent (iOS: "<App>/<build> CFNetwork/…
 * Darwin/…", Android: "okhttp/…"). Anything a browser recognisably sent is
 * web (desktop and mobile browsers alike). Everything else is unknown.
 */
export const classifyUserAgent = ua => {
    if (!ua || typeof ua !== 'string' || !ua.trim()) return { platform: null, label: null };
    if (/okhttp\/|Dalvik\//i.test(ua)) return { platform: 'mobile', label: 'App · Android' };
    if (/CFNetwork\/|Darwin\//.test(ua)) return { platform: 'mobile', label: 'App · iOS' };
    const { browser, label } = describeUserAgent(ua);
    if (browser) return { platform: 'web', label };
    return { platform: null, label };
};

const PLATFORM_LABELS = { web: 'Web', mobile: 'Mobile' };

// Same classes (and so the same colours) as the Guest Visitors platform pill.
const PlatformPill = ({ platform }) => {
    const label = PLATFORM_LABELS[platform];
    if (!label) return <span className="sk-muted">{PLACEHOLDER}</span>;
    return <span className={`sk-pill sk-platform-pill is-${platform}`}>{label}</span>;
};

const TruncCell = ({ value, className = '' }) => {
    const text = cellText(value);
    return (
        <td className={`sk-cell-truncate ${className}`.trim()} title={text === PLACEHOLDER ? undefined : text}>
            {text}
        </td>
    );
};

const COLUMNS = [
    'User', 'Phone', 'Status', 'New', 'Verified', 'Role', 'Location', 'Language', 'Platform',
    'Device', 'App version', 'Logins', 'Activity', 'First seen', 'Last sign-in', 'Joined', 'Last IP',
];
const NUMERIC = new Set(['Logins', 'Activity']);

const UserRow = ({ row }) => {
    const user = (row && row.user && typeof row.user === 'object') ? row.user : {};
    const name = resolveName(user);
    const userId = row && row.userId ? String(row.userId) : '';
    const href = userId ? `/#/users/${userId}/show` : undefined;
    const location = fmtLocation(user.city, user.country);
    const locationSet = location !== 'Location not set';
    const lang = cellText(user.language);
    const role = cellText(user.role);
    const status = cellText(user.status);
    const ua = typeof row.lastUserAgent === 'string' && row.lastUserAgent.trim() ? row.lastUserAgent : null;
    const client = classifyUserAgent(ua);
    const appVersion = cellText(user.lastAppVersion);
    const appVersionAt = fmtDate(user.lastAppVersionAt);
    const joinedNotReturned = Boolean(row.registeredInWindow) && row.loginCount === 0;

    return (
        <tr data-testid="rau-row">
            <td className="sk-rau-user" title={name}>
                <Avatar src={user.profilePic || undefined} alt={name} size="sm" />
                {href ? <a href={href}>{name}</a> : <span>{name}</span>}
            </td>
            <td>{cellText(user.phone)}</td>
            <td>{status === PLACEHOLDER ? PLACEHOLDER : <StatusPill status={status}>{status}</StatusPill>}</td>
            <td>{row.registeredInWindow ? <span className="sk-pill sk-rau-pill is-new">New</span> : PLACEHOLDER}</td>
            <td>{verifiedLabel(user)}</td>
            <td>{role.toLowerCase() === 'admin' ? <span className="sk-pill sk-rau-pill is-admin">Admin</span> : role}</td>
            <td className="sk-cell-truncate" title={location}>
                {locationSet ? location : <span className="sk-muted">Not set</span>}
            </td>
            <td>{lang === PLACEHOLDER ? lang : lang.toUpperCase()}</td>
            <td><PlatformPill platform={client.platform} /></td>
            <td className="sk-cell-truncate" title={ua ? `Guessed from user agent: ${ua}` : undefined}>
                {client.label ? <span className="sk-muted">{client.label}</span> : PLACEHOLDER}
            </td>
            <td title={appVersion !== PLACEHOLDER && appVersionAt !== PLACEHOLDER ? `Reported ${appVersionAt}` : undefined}>{appVersion}</td>
            <td className="sk-num">{fmtCount(row.loginCount)}</td>
            <td className="sk-num">{fmtCount(row.activityCount)}</td>
            <td>{fmtDate(row.firstActivityAt)}</td>
            <td>{joinedNotReturned ? <span className="sk-muted">Joined, not returned yet</span> : fmtDate(row.lastLoginAt)}</td>
            <td>{fmtDate(user.createdAt)}</td>
            <TruncCell value={row.lastIp} />
        </tr>
    );
};

export default function RecentlyActiveUsers() {
    const { permissions, loaded } = usePermissions();
    const roles = (permissions && permissions.adminRoles) || [];
    const allowed = loaded && hasAccess('recently-active-users', roles);
    const [days, setDays] = useState(30);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef(null);

    // Debounce search input
    const handleSearchChange = useCallback((e) => {
        const val = e.target.value;
        setSearch(val);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(val);
            setPage(1);
        }, 400);
    }, []);

    useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

    useEffect(() => {
        if (!allowed) return undefined;
        let alive = true;
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({ days, page, limit: 25 });
        if (debouncedSearch) params.set('search', debouncedSearch);
        httpClient(`${API_BASE_URL}/admin/recently-active-users?${params}`, { method: 'GET', cache: 'no-store' })
            .then(({ json }) => { if (alive) setData(json); })
            .catch(e => { if (alive) setError(e && e.status === 403 ? 'You do not have access to this page.' : 'Could not load recently active users.'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [days, page, debouncedSearch, allowed]);

    // Reset page when days changes
    useEffect(() => { setPage(1); }, [days]);

    if (!loaded) return <PageShell><StatePanel state="loading">Loading permissions…</StatePanel></PageShell>;
    if (!hasAccess('recently-active-users', roles)) return <PageShell><StatePanel state="error">You do not have permission to view this page.</StatePanel></PageShell>;

    const rangeTabs = (
        <div className="sk-dashboard-chart-tabs" role="group" aria-label="Date range">
            {RANGES.map(d => (
                <button key={d} type="button" aria-pressed={days === d} className={days === d ? 'active' : ''} onClick={() => setDays(d)}>{d} days</button>
            ))}
        </div>
    );

    const totalPages = (data && data.totalPages) || 1;
    const rows = (data && Array.isArray(data.rows)) ? data.rows : [];

    return (
        <main className="sk-page">
            <PageHeader eyebrow="Insights" title="Recently Active & New Users"
                description="Users who signed in or registered during the selected window. Registrations and sign-ins are both counted; there is no backfill so nothing before the feature shipped is included."
                actions={rangeTabs} />

            {loading && <StatePanel state="loading">Loading recently active users…</StatePanel>}
            {error && <StatePanel state="error">{error}</StatePanel>}

            {data && (
                <>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                        <MetricCard tone="purple" label="Distinct users" value={fmtInt(data.totalUsers)} detail={`Active in last ${days} days`} />
                        <MetricCard tone="blue" label="Total logins" value={fmtInt(data.totalLogins)} detail={`LOGIN events in window`} />
                        <MetricCard tone="orange" label="New registrations" value={fmtInt(data.totalRegistrations)} detail={`Joined in last ${days} days`} />
                    </div>

                    <Surface title="Active & new users" description={`Last ${days} days`}
                        actions={<Search value={search} onChange={handleSearchChange} placeholder="Search name or phone…" />}>

                        <p className="sk-muted" style={{ margin: '0 0 12px', fontSize: 12, lineHeight: 1.5 }}>
                            <strong>Note:</strong> Registrations and sign-ins are both counted. City and Country are the user's profile location (set once during profile setup via expo-location), not the location they signed in from. Platform and Device are a best guess from the user agent of the user's last sign-in in the window; Last IP is from that same sign-in. Fields a user has not reported show —. There is no backfill, so activity before this feature shipped is not included.
                        </p>

                        {rows.length === 0 && <Empty>No recently active users in this window.</Empty>}

                        {rows.length > 0 && (
                            <div className="sk-table-wrap">
                                <table className="sk-data-table sk-guest-table sk-rau-table">
                                    <thead>
                                        <tr>
                                            {COLUMNS.map(c => <th key={c} className={NUMERIC.has(c) ? 'sk-num' : undefined}>{c}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, i) => (
                                            <UserRow key={(row && row.userId) || `row-${i}`} row={row || {}} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                                <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                                    style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid var(--sk-border)', background: 'var(--sk-surface)', cursor: page <= 1 ? 'default' : 'pointer' }}>
                                    Previous
                                </button>
                                <span style={{ padding: '6px 14px', fontSize: 13 }}>Page {page} of {totalPages}</span>
                                <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                                    style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid var(--sk-border)', background: 'var(--sk-surface)', cursor: page >= totalPages ? 'default' : 'pointer' }}>
                                    Next
                                </button>
                            </div>
                        )}
                    </Surface>
                </>
            )}
        </main>
    );
}
